/**
 * Registro de capacidades por idioma: qué superficies del producto están
 * PUBLICADAS en cada locale.
 *
 * "Soportado" y "publicado" son conceptos distintos: un idioma entra al
 * contrato en `server/domain/language.ts` (`PREFERRED_LANGUAGES`) y desde ese
 * momento router, API y UI lo aceptan; pero puede todavía no tener guías,
 * tracker o landings del catálogo. Esa diferencia vive AQUÍ y solo aquí: cada
 * familia de rutas deriva su lista (`GUIDE_LOCALES`, `TRACKER_LOCALES`,
 * `LANDING_LOCALES`) de este registro en vez de declarar la suya propia.
 *
 * Las intros habladas son la excepción deliberada: su publicación es POR
 * PERSONAJE (manifiesto generado en lib/character-intros/manifest.generated.ts),
 * no un conjunto de locales plano, así que no cabe en este registro.
 *
 * Este módulo llega al bundle cliente (LanguageSelector vía lib/locale-paths):
 * constantes planas, sin imports de contenido editorial.
 */

import { getLocalePrefix, PUBLIC_LOCALES, toPublicLocale, type PublicLocale } from '@/lib/locale-prefix'

/** Superficies con publicación por idioma. */
export const LOCALE_SURFACE = ['generic-landings', 'guides', 'tracker', 'statistics', 'custom-landing'] as const

export type LocaleSurface = (typeof LOCALE_SURFACE)[number]

/**
 * Locales con cada superficie publicada. Tupla explícita a propósito, NO
 * `PUBLIC_LOCALES`: publicar una superficie en un idioma nuevo es una decisión
 * consciente (requiere contenido editorial, rutas Next y assets), y el test del
 * registro impide marcarla publicada sin haberlas aportado.
 */
const GENERIC_LANDINGS_PUBLISHED = ['es', 'en', 'pt', 'it', 'fr', 'ja', 'de'] as const satisfies readonly PublicLocale[]
const GUIDES_PUBLISHED = ['es', 'en', 'pt', 'it', 'fr', 'ja', 'de'] as const satisfies readonly PublicLocale[]
const TRACKER_PUBLISHED = ['es', 'en', 'pt', 'it', 'fr', 'ja', 'de'] as const satisfies readonly PublicLocale[]
const STATISTICS_PUBLISHED = ['es'] as const satisfies readonly PublicLocale[]
// El clip del hero es un asset visual compartido y sin pista de audio
// localizada (ver `heroVideoLocale: 'shared'` en el contrato de copy), así que
// no introduce una mezcla de idioma al publicar el copy traducido.
const CUSTOM_LANDING_PUBLISHED = ['es', 'en', 'pt', 'it', 'fr', 'ja', 'de'] as const satisfies readonly PublicLocale[]

export {
  GENERIC_LANDINGS_PUBLISHED,
  GUIDES_PUBLISHED,
  TRACKER_PUBLISHED,
  STATISTICS_PUBLISHED,
  CUSTOM_LANDING_PUBLISHED,
}

const PUBLISHED_BY_SURFACE: Record<LocaleSurface, readonly PublicLocale[]> = {
  'generic-landings': GENERIC_LANDINGS_PUBLISHED,
  guides: GUIDES_PUBLISHED,
  tracker: TRACKER_PUBLISHED,
  statistics: STATISTICS_PUBLISHED,
  'custom-landing': CUSTOM_LANDING_PUBLISHED,
}

export const STATISTICS_PATH = '/estadisticas/dientes-de-leche'
/** Path canónico (es) de la landing del personaje propio. */
export const CUSTOM_LANDING_PATH = '/tu-personaje'
/** Segmento del `?from=` que las landings fuera del catálogo usan en el auth. */
export const CUSTOM_LANDING_SEGMENT = CUSTOM_LANDING_PATH.slice(1)

const SURFACE_PATHS: ReadonlyArray<{ surface: LocaleSurface; path: string }> = [
  { surface: 'statistics', path: STATISTICS_PATH },
  { surface: 'custom-landing', path: CUSTOM_LANDING_PATH },
]

/** Locales con la superficie publicada, en orden canónico del contrato. */
export function publishedLocalesOf(surface: LocaleSurface): readonly PublicLocale[] {
  return PUBLIC_LOCALES.filter((locale) => PUBLISHED_BY_SURFACE[surface].includes(locale))
}

/** ¿Está la superficie publicada en el locale? Matching exacto contra la
 *  tupla publicada: SIN fallback al default. Normalizar aquí (`toPublicLocale`)
 *  colaría cualquier entrada fuera de contrato —p. ej. `fr`→`es`— y publicaría
 *  una superficie que no existe en ese mercado. */
export function isPublished(locale: unknown, surface: LocaleSurface): boolean {
  return (
    typeof locale === 'string' &&
    (PUBLISHED_BY_SURFACE[surface] as readonly string[]).includes(locale)
  )
}

/**
 * Href de una superficie publicada en ese locale, o `undefined` si no lo está.
 * Puro: el gate de despliegue (la env del rollout, p. ej. el de la landing del
 * personaje propio) es decisión de quien lo consume — footer, página o sitemap —
 * y este módulo no lo conoce.
 */
export function surfaceHref(surface: LocaleSurface, locale: string): string | undefined {
  const entry = SURFACE_PATHS.find((candidate) => candidate.surface === surface)
  if (!entry || !isPublished(toPublicLocale(locale), surface)) return undefined
  return `${getLocalePrefix(toPublicLocale(locale))}${entry.path}`
}

/** Superficie representada por un segmento de `?from=` (sin barra), si existe. */
export function surfaceForSegment(segment: string | undefined): LocaleSurface | undefined {
  if (!segment) return undefined
  return SURFACE_PATHS.find((entry) => entry.path === `/${segment}`)?.surface
}
function withoutLocalePrefix(pathname: string): string {
  const normalized = `/${pathname.replace(/^\/+|\/+$/g, '')}`
  const localePrefix = PUBLIC_LOCALES.map((locale) => getLocalePrefix(locale) || `/${locale}`).find(
    (prefix) => prefix && (normalized === prefix || normalized.startsWith(`${prefix}/`)),
  )

  return localePrefix ? normalized.slice(localePrefix.length) || '/' : normalized
}

/** Returns the published surface represented by a public pathname, if known. */
export function localeSurfaceForPath(pathname: string): LocaleSurface | undefined {
  const path = withoutLocalePrefix(pathname)
  return SURFACE_PATHS.find((entry) => entry.path === path)?.surface
}

/**
 * A locale selector is useful only when the current surface has more than one
 * published locale. Unknown paths remain enabled by default.
 */
export function isLocaleSelectorEnabled(pathname: string): boolean {
  const surface = localeSurfaceForPath(pathname)
  return surface === undefined || publishedLocalesOf(surface).length > 1
}
